import { defineStore } from 'pinia';
import { ref } from 'vue';
import { soloApi } from '@/utils/api';
import type { SoloDailyState, SoloPropositionOut, SoloRevealedPropositionOut } from '@/utils/api';

export const useSoloStore = defineStore('solo', () => {
  const daily = ref<SoloDailyState | null>(null);
  const loadingDaily = ref(false);

  const proposition = ref<SoloPropositionOut | null>(null);
  const index = ref(0);
  const total = ref(10);
  const score = ref(0);
  const durationMs = ref(0);
  const finished = ref(false);
  const submitting = ref(false);
  const lastResult = ref<{ correct: boolean; timedOut: boolean; answeredProposition: SoloRevealedPropositionOut | null } | null>(null);
  // Horodatage client (Date.now()) de réception de la proposition en cours — sert
  // uniquement au décompte visuel ; le serveur est seul autoritaire sur le score/temps.
  const propSentAt = ref<number | null>(null);

  async function fetchDaily() {
    loadingDaily.value = true;
    try {
      daily.value = await soloApi.getDaily();
    } finally {
      loadingDaily.value = false;
    }
  }

  async function start() {
    const res = await soloApi.start();
    index.value = res.index;
    total.value = res.total;
    score.value = res.score;
    durationMs.value = res.duration_ms;
    proposition.value = res.proposition;
    finished.value = false;
    lastResult.value = null;
    propSentAt.value = Date.now();
  }

  async function submit(value: any) {
    if (!proposition.value || submitting.value) return;
    submitting.value = true;
    try {
      const res = await soloApi.answer(proposition.value.id, value);
      index.value = res.index;
      total.value = res.total;
      score.value = res.score;
      durationMs.value = res.duration_ms;
      finished.value = res.finished;
      lastResult.value = { correct: res.correct, timedOut: res.timed_out, answeredProposition: res.answered_proposition };
      proposition.value = res.next;
      // Ne pas re-timestamper ici : la question suivante n'est pas encore affichée
      // (l'utilisateur peut rester longtemps sur l'écran de feedback avant de cliquer
      // "Suivant"). L'horodatage est posé au moment réel de l'affichage, cf. markShown().
      propSentAt.value = null;
      if (res.finished && daily.value) {
        daily.value = {
          ...daily.value,
          already_played: true,
          in_progress: false,
          score: score.value,
          duration_ms: durationMs.value,
        };
      }
    } finally {
      submitting.value = false;
    }
  }

  function markShown() {
    propSentAt.value = proposition.value ? Date.now() : null;
  }

  function reset() {
    proposition.value = null;
    index.value = 0;
    score.value = 0;
    durationMs.value = 0;
    finished.value = false;
    lastResult.value = null;
    propSentAt.value = null;
  }

  return {
    daily, loadingDaily,
    proposition, index, total, score, durationMs, finished, submitting, lastResult, propSentAt,
    fetchDaily, start, submit, markShown, reset,
  };
});
